import React, { useEffect, useRef, useState } from "react";

// Modules
import { useSelector } from "react-redux";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToWindowEdges,
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";

// App
import { serverUrl } from "../../config";
import moment from "moment";
import { DEFAULT_DASHBOARD_TITLE } from "./salesRoomData";
import RoomUpdateCards from "../room/roomUpdateCards";
import MediaLibrary from "../../partials/mediaLibrary";
import CategorySelector from "../../partials/categorySelector";
import EntitySelector from "../training/entitySelector";
import MilestoneBuilder from "./milestoneBuilder";
import AccentPicker from "./partials/accentPicker";

// UI components
import { Col, Row } from "react-bootstrap";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  Box,
  // Container,
  Stack,
} from "@mui/material";
import Container from "react-bootstrap/Container";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DragIcon from "@mui/icons-material/DragIndicatorOutlined";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import MovieIcon from "@mui/icons-material/MovieOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import SendLinkIcon from "@mui/icons-material/ForwardToInboxOutlined";
import SlideshowIcon from "@mui/icons-material/SlideshowOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import LinkIcon from "@mui/icons-material/LinkOutlined";
import MicIcon from "@mui/icons-material/MicNoneRounded";
import CollectionsIcon from "@mui/icons-material/CollectionsOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import EventIcon from "@mui/icons-material/CalendarTodayOutlined";
import VideoRecorder from "./videoRecorder";
import AlertModal from "../../partials/alertModal";

// Content types a buyer can consume anonymously - keep in sync with the dsr
// module's SalesRoom::referenceFieldSettings().
export const ROOM_CONTENT_TYPES = [
  "presentation",
  "document",
  "video",
  "link",
  "podcast",
  "gallery",
  "news",
  "event",
];

// Icon + human label per content type, mirroring buyerRoom.js so the seller's
// cards read exactly like the buyer's.
const typeMeta = {
  presentation: { icon: SlideshowIcon, label: "Presentation" },
  document: { icon: DescriptionIcon, label: "Document" },
  video: { icon: MovieIcon, label: "Video" },
  link: { icon: LinkIcon, label: "Link" },
  podcast: { icon: MicIcon, label: "Podcast" },
  gallery: { icon: CollectionsIcon, label: "Gallery" },
  news: { icon: ArticleIcon, label: "News" },
  event: { icon: EventIcon, label: "Event" },
};

const ROOM_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
];

const ALERT_MODES = [
  { value: "off", label: "Off" },
  { value: "first_open", label: "First visit only" },
  { value: "every_open", label: "Every visit" },
];

const ACCESS_MODES = [
  { value: "link", label: "Anyone with the link" },
  { value: "password", label: "Anyone with the link + passcode" },
  { value: "restricted", label: "Invited people only" },
];

// The two uploadable brand marks, in masthead order. `key` doubles as the
// media-picker mode.
const LOGO_SLOTS = [
  {
    key: "companyLogo",
    label: "Company logo",
    fidField: "companyLogoFid",
    urlField: "companyLogoUrl",
  },
  {
    key: "logo",
    label: "Client logo",
    fidField: "logoFid",
    urlField: "logoUrl",
  },
];

// Buyer-facing status wording, matching the chips in the room.
const MILESTONE_STATUS_LABELS = {
  pending: "Not started",
  in_progress: "In progress",
  done: "Completed",
};

const newSectionId = () =>
  `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createSection = () => ({
  id: newSectionId(),
  title: "",
  description: "",
  items: [],
});

const itemKey = (item) => `${item.type || item.bundle}:${item.id}`;

const thumbUrl = (thumbnail) => {
  if (!thumbnail) {
    return null;
  }
  return thumbnail.indexOf("http") === 0
    ? thumbnail
    : `${serverUrl}${thumbnail}`;
};

const logoUrl = (logo) => {
  if (!logo) {
    return null;
  }
  return logo.indexOf("http") === 0 ? logo : `${serverUrl}${logo}`;
};

/**
 * A borderless textarea that grows to fit its content instead of scrolling,
 * so the hero expands exactly like the buyer's (which has no fixed height).
 */
function AutoGrowTextarea({ value, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [value]);
  return <textarea ref={ref} value={value} {...rest} />;
}

/**
 * A content card that can be dragged to reorder within its section.
 */
function SortableCard({ item, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemKey(item) });
  const meta = typeMeta[item.type] || typeMeta.document;
  const TypeIcon = meta.icon;
  const src = thumbUrl(item.thumbnail);
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="col-12 col-sm-6 col-lg-3 sales-room-canvas__card-col"
    >
      {/* The whole card is the drag handle; the remove button stops the
          pointer so a click there never starts a drag. */}
      <div
        className="buyer-room__card sales-room-canvas__card"
        {...attributes}
        {...listeners}
      >
        <div className="sales-room-canvas__card-tools">
          <Tooltip title="Remove from section">
            <IconButton
              size="small"
              onClick={onRemove}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <div className="buyer-room__card-thumbnail">
          {src ? (
            <img src={src} alt={item.title} />
          ) : (
            <TypeIcon className="buyer-room__card-icon" />
          )}
          <span className="buyer-room__card-type-chip">{meta.label}</span>
        </div>
        <div className="buyer-room__card-body">
          <Typography
            owner="admin"
            variant="body1"
            className="buyer-room__card-title"
          >
            {item.title}
          </Typography>
          <Typography
            owner="admin"
            variant="body2"
            className="buyer-room__card-summary"
          >
            {item.summary}
          </Typography>
        </div>
      </div>
    </div>
  );
}

/**
 * One content section inside the Resources tab: an editable heading and
 * standfirst, the draggable card grid, and an add-content tile. The section
 * itself is draggable by its grip so sellers can reorder the stack, matching
 * the order buyers scroll through.
 */
function SortableSection({
  section,
  sensors,
  onChange,
  onRemove,
  onRemoveItem,
  onAddContent,
  onCardDragEnd,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <section
      ref={setNodeRef}
      style={style}
      className="sales-room-canvas__section"
    >
      <div className="sales-room-canvas__section-head">
        <Tooltip title="Drag to reorder this section">
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="sales-room-canvas__section-grip"
            {...attributes}
            {...listeners}
          >
            <DragIcon fontSize="small" />
          </button>
        </Tooltip>
        <div className="sales-room-canvas__section-headings">
          <input
            className="sales-room-canvas__section-title-input"
            value={section.title}
            placeholder="Section title"
            onChange={(e) => onChange({ title: e.target.value })}
          />
          <input
            className="sales-room-canvas__section-desc-input"
            value={section.description}
            placeholder="Section description (optional)"
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </div>
        <div className="sales-room-canvas__section-tools">
          <Tooltip title="Delete section">
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onCardDragEnd}
        modifiers={[restrictToWindowEdges]}
      >
        <SortableContext
          items={section.items.map(itemKey)}
          strategy={rectSortingStrategy}
        >
          <Row className="gy-3">
            {section.items.map((item, index) => (
              <SortableCard
                key={itemKey(item)}
                item={item}
                onRemove={() => onRemoveItem(index)}
              />
            ))}
            <div className="col-12 col-sm-6 col-lg-3">
              <Tooltip title="Add content to this section">
                <button
                  type="button"
                  className="sales-room-canvas__add-card"
                  onClick={onAddContent}
                >
                  <AddIcon />
                  <Typography owner="admin" variant="body2">
                    Add content
                  </Typography>
                </button>
              </Tooltip>
            </div>
          </Row>
        </SortableContext>
      </DndContext>
    </section>
  );
}

/**
 * Visual, buyer-styled editor for a sales room. Renders the same hero /
 * tabbed-sections / content-card layout the buyer sees (reusing the
 * `buyer-room__*` classes + the `--dsr-accent` variable), but every element is
 * editable in place: type into the hero, drag content cards / sections to
 * reorder, rename sections. Non-visual room settings (status, access, alerts,
 * contacts) live in the Settings dialog, opened from the edit-screen toolbar.
 *
 * It reads and writes the exact same `form` shape SalesRoomEdit builds, so
 * buildRoomPayload()/sectionsFromRoom()/milestonesFromRoom() are unchanged.
 */
export default function SalesRoomCanvas(props) {
  const { form, setForm, onSendAccessLink, showSettings, setShowSettings } =
    props;

  const user = useSelector((state) => state).authReducer.user;
  const permissions = useSelector((state) => state).permissionsReducer
    .permissions;
  const canManageTemplates = permissions?.manage_sales_room_templates === true;

  const [activeTabKey, setActiveTabKey] = useState("dashboard");
  // Which media picker is open: 'logo' | 'video' | null.
  const [mediaPicker, setMediaPicker] = useState(null);
  // Content picker: the section id it will add to, or null when closed.
  const [selectorSectionId, setSelectorSectionId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [introVideoDialog, setIntroVideoDialog] = useState(false);
  const [tempVideo, setTempVideo] = useState(null);
  const [ryoVideo, setRyoVideo] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [alertDetails, setAlertDetails] = useState({
    show: false,
    title: "",
    message: "",
    cancelLabel: "",
    confirmLabel: "",
    cancel: () => {},
    confirm: () => {},
  });

  const recorderRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const accent = form.colour || "#0e6ba8";

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  // --- Section + item operations -------------------------------------------
  const setSections = (sections) => setField("sections", sections);

  const updateSection = (id, changes) =>
    setSections(
      form.sections.map((section) =>
        section.id === id ? { ...section, ...changes } : section,
      ),
    );

  const addSection = () => {
    setSections([...form.sections, createSection()]);
    setActiveTabKey("resources");
  };

  const removeSection = (id) =>
    setSections(form.sections.filter((section) => section.id !== id));

  const removeItem = (section, index) =>
    updateSection(section.id, {
      items: section.items.filter((_, i) => i !== index),
    });

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = form.sections.findIndex((s) => s.id === active.id);
    const newIndex = form.sections.findIndex((s) => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    setSections(arrayMove(form.sections, oldIndex, newIndex));
  };

  const handleCardDragEnd = (section) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = section.items.findIndex((i) => itemKey(i) === active.id);
    const newIndex = section.items.findIndex((i) => itemKey(i) === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    updateSection(section.id, {
      items: arrayMove(section.items, oldIndex, newIndex),
    });
  };

  // --- Contacts (settings dialog) ------------------------------------------
  const updateContact = (index, field, value) =>
    setField(
      "contacts",
      form.contacts.map((contact, i) =>
        i === index ? { ...contact, [field]: value } : contact,
      ),
    );

  // --- Derived --------------------------------------------------------------
  const sections = form.sections || [];
  // Fixed tab bar, mirroring the buyer's: every content section lives in the
  // one Resources tab rather than getting a tab of its own.
  const tabs = [
    { key: "dashboard", label: "Dashboard" },
    { key: "resources", label: "Resources" },
    { key: "milestones", label: "Action Plan" },
  ];
  const activeTab = tabs.find((tab) => tab.key === activeTabKey) || tabs[0];
  const selectorSection = sections.find(
    (section) => section.id === selectorSectionId,
  );

  const doneMilestones = (form.milestones || []).filter(
    (milestone) => milestone.status === "done",
  ).length;
  const totalMilestones = (form.milestones || []).length;

  // A preview of the buyer's "Latest updates" cards, in the same shape the
  // portal builds (see buyerRoom.js) so RoomUpdateCards renders both
  // identically. The buyer prefers its activity feed and falls back to
  // current state; there is no feed here, so this is the current state only.
  const previewItems = sections.flatMap((section) =>
    (section.items || []).map((item) => ({
      key: `item-${item.type}-${item.id}`,
      title: item.title,
      meta: [section.title, (typeMeta[item.type] || typeMeta.document).label]
        .filter(Boolean)
        .join(" · "),
    })),
  );
  const previewSteps = (form.milestones || []).filter(
    (milestone) => milestone.title,
  );
  const previewOpenSteps = previewSteps
    .filter((milestone) => milestone.status !== "done")
    .sort(
      (a, b) =>
        (a.dueInput ? moment(a.dueInput).unix() : Infinity) -
        (b.dueInput ? moment(b.dueInput).unix() : Infinity),
    );

  const updatePreview = [
    {
      key: "resources",
      label: "Resources",
      show: previewItems.length > 0,
      lines: previewItems.slice(0, 3),
      fallback: `${previewItems.length} item${
        previewItems.length === 1 ? "" : "s"
      } shared.`,
    },
    {
      key: "milestones",
      label: "Action plan",
      show: previewSteps.length > 0,
      lines: previewOpenSteps.slice(0, 3).map((milestone) => ({
        key: `step-${milestone.id}`,
        title: milestone.title,
        meta: [
          milestone.dueInput
            ? `Due ${moment(milestone.dueInput).format("D MMM YYYY")}`
            : null,
          MILESTONE_STATUS_LABELS[milestone.status] || "Not started",
        ]
          .filter(Boolean)
          .join(" · "),
      })),
      fallback: `All ${previewSteps.length} step${
        previewSteps.length === 1 ? "" : "s"
      } complete.`,
    },
    {
      // The buyer's third card. The thread only exists once the room is live,
      // so it is always shown here with its standby copy rather than hidden -
      // the seller needs to see the layout buyers will get.
      key: "messages",
      label: "Messages",
      show: true,
      lines: [],
      fallback: "Buyer messages appear here once the room is live.",
    },
  ].filter((card) => card.show);

  const resetAlert = () => {
    setAlertDetails({
      show: false,
      title: "",
      message: "",
      cancelLabel: "",
      confirmLabel: "",
      cancel: () => {},
      confirm: () => {},
    });
  };

  // Fired by VideoRecorder once the upload finishes: select the new video
  const handleRecordingUploaded = ({ mid, name }) => {
    const video = { introVideoMid: mid, introVideoName: name };
    setHasRecording(false);
    setRyoVideo(false);
    setForm((prev) => ({ ...prev, ...video }));
    setTempVideo(video);
  };

  const finishSave = (video) => {
    setRyoVideo(false);
    setIntroVideoDialog(false);
    if (video) {
      setForm((prev) => ({ ...prev, ...video }));
    }
    setTempVideo(null);
  };

  const handleVideoSave = () => {
    if (ryoVideo && hasRecording && recorderRef.current) {
      setAlertDetails({
        show: true,
        title: "Upload recording?",
        message:
          "Your recording has not been uploaded yet. Upload it and use it as the intro video before saving?",
        cancelLabel: "Cancel",
        confirmLabel: "Confirm",
        cancel: resetAlert,
        confirm: async () => {
          resetAlert();
          try {
            const { mid, name } = await recorderRef.current.upload();
            finishSave({ introVideoMid: mid, introVideoName: name });
          } catch (e) {
            // VideoRecorder already showed the error; the dialog stays open
          }
        },
      });
      return;
    }
    finishSave(tempVideo);
  };

  return (
    <div className="sales-room-canvas" style={{ "--dsr-accent": accent }}>
      {/* Masthead, exactly as the buyer sees it: our logo left, the room
          title centred with the tabs beneath, the client's logo right. Every
          part of it is edited in place. */}
      <header className="buyer-room__masthead sales-room-canvas__masthead">
        <div className="buyer-room__masthead-inner">
          <div className="buyer-room__masthead-brand buyer-room__masthead-brand--seller">
            {LOGO_SLOTS.filter((slot) => slot.key === "companyLogo").map(
              (slot) => (
                <div className="sales-room-canvas__logo-slot" key={slot.key}>
                  {form[slot.urlField] ? (
                    <div className="sales-room-canvas__logo">
                      <img
                        src={logoUrl(form[slot.urlField])}
                        alt={slot.label}
                      />
                      <div className="sales-room-canvas__logo-actions">
                        <Tooltip title={`Change ${slot.label.toLowerCase()}`}>
                          <IconButton
                            size="small"
                            onClick={() => setMediaPicker(slot.key)}
                          >
                            <ImageIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={`Remove ${slot.label.toLowerCase()}`}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setField(slot.fidField, null);
                              setField(slot.urlField, null);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    <Tooltip title={`Add a ${slot.label.toLowerCase()}`}>
                      <button
                        type="button"
                        className="sales-room-canvas__logo-add"
                        onClick={() => setMediaPicker(slot.key)}
                      >
                        <ImageIcon fontSize="small" /> {slot.label}
                      </button>
                    </Tooltip>
                  )}
                </div>
              ),
            )}
          </div>

          <div className="buyer-room__masthead-centre">
            <input
              className="sales-room-canvas__hero-field sales-room-canvas__title-input"
              value={form.title}
              placeholder="Room title"
              onChange={(e) => setField("title", e.target.value)}
            />
            <div
              className="buyer-room__tabs sales-room-canvas__tabs"
              role="tablist"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab.key === tab.key}
                  className={`buyer-room__tab ${
                    activeTab.key === tab.key ? "buyer-room__tab--active" : ""
                  }`}
                  onClick={() => setActiveTabKey(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="buyer-room__masthead-brand buyer-room__masthead-brand--buyer">
            {LOGO_SLOTS.filter((slot) => slot.key === "logo").map((slot) => (
              <div className="sales-room-canvas__logo-slot" key={slot.key}>
                {form[slot.urlField] ? (
                  <div className="sales-room-canvas__logo">
                    <img src={logoUrl(form[slot.urlField])} alt={slot.label} />
                    <div className="sales-room-canvas__logo-actions">
                      <Tooltip title={`Change ${slot.label.toLowerCase()}`}>
                        <IconButton
                          size="small"
                          onClick={() => setMediaPicker(slot.key)}
                        >
                          <ImageIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={`Remove ${slot.label.toLowerCase()}`}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setField(slot.fidField, null);
                            setField(slot.urlField, null);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                ) : (
                  <Tooltip title={`Add a ${slot.label.toLowerCase()}`}>
                    <button
                      type="button"
                      className="sales-room-canvas__logo-add"
                      onClick={() => setMediaPicker(slot.key)}
                    >
                      <ImageIcon fontSize="small" /> {slot.label}
                    </button>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        </div>
      </header>

      <div className="sales-room-canvas__body">
        {/* ---- Dashboard tab: the branded hero, edited in place ------------- */}
        {activeTab.key === "dashboard" && (
          <section className="sales-room-canvas__hero mb-4">
            <div className="sales-room-canvas__hero-toolbar">
              <Tooltip title="Accent colour">
                <span className="sales-room-canvas__accent">
                  <AccentPicker
                    value={accent}
                    onChange={(hex) => setField("colour", hex)}
                  />
                </span>
              </Tooltip>
            </div>
            {/* The buyer sees this as the dashboard heading; blank falls
              back to the default rather than rendering an empty line. */}
            <input
              className="sales-room-canvas__dashboard-title-input"
              value={form.dashboardTitle}
              placeholder={DEFAULT_DASHBOARD_TITLE}
              maxLength={255}
              onChange={(e) => setField("dashboardTitle", e.target.value)}
            />
            <Row className="g-4 align-items-stretch">
              <Col xs={12} lg={7}>
                <div className="sales-room-canvas__welcome-card">
                  <div className="sales-room-canvas__company-line">
                    <span>Prepared for</span>
                    <input
                      className="sales-room-canvas__hero-field sales-room-canvas__company-input"
                      value={form.company}
                      placeholder="company name"
                      onChange={(e) => setField("company", e.target.value)}
                    />
                  </div>

                  <AutoGrowTextarea
                    className="sales-room-canvas__hero-field sales-room-canvas__lead-input"
                    value={form.description}
                    placeholder="Add a short welcome message for the buyer..."
                    onChange={(e) => setField("description", e.target.value)}
                  />
                </div>
              </Col>
              <Col xs={12} lg={5}>
                <div className="sales-room-canvas__intro-card">
                  {form.introVideoMid ? (
                    <>
                      <div className="buyer-room__intro-video sales-room-canvas__intro-video">
                        <div className="sales-room-canvas__intro-placeholder">
                          <MovieIcon />
                          <Typography owner="admin" variant="body2">
                            {form.introVideoName ||
                              `Video ${form.introVideoMid}`}
                          </Typography>
                        </div>
                      </div>
                      <div className="sales-room-canvas__intro-actions">
                        <Tooltip title="Change intro video">
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "none" }}
                            onClick={() => {
                              setIntroVideoDialog(true);
                              if (form?.introVideoMid && form?.introVideoName) {
                                const tempData = {
                                  introVideoMid: form.introVideoMid,
                                  introVideoName: form?.introVideoName,
                                };
                                setTempVideo({ ...tempData });
                              }
                            }}
                          >
                            Change
                          </Button>
                        </Tooltip>
                        <Tooltip title="Remove intro video">
                          <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "none" }}
                            onClick={() => {
                              setField("introVideoMid", null);
                              setField("introVideoName", null);
                            }}
                          >
                            Remove
                          </Button>
                        </Tooltip>
                      </div>
                    </>
                  ) : (
                    <Tooltip title="Add an intro video">
                      <button
                        type="button"
                        className="sales-room-canvas__intro-add"
                        onClick={() => setIntroVideoDialog(true)}
                      >
                        <MovieIcon />
                        <Typography owner="admin" variant="body2">
                          Add an intro video
                        </Typography>
                        <Typography owner="admin" variant="caption">
                          A short personal introduction shown at the top of the
                          buyer's room.
                        </Typography>
                      </button>
                    </Tooltip>
                  )}
                  <input
                    className="sales-room-canvas__intro-caption"
                    value={form.introText}
                    placeholder="Intro caption (optional)"
                    maxLength={255}
                    onChange={(e) => setField("introText", e.target.value)}
                  />
                </div>
              </Col>
            </Row>
          </section>
        )}

        {activeTab.key === "dashboard" && (
          <RoomUpdateCards cards={updatePreview} owner="admin" />
        )}

        {/* ---- Resources tab: every content section, stacked and sortable -- */}
        {activeTab.key === "resources" && (
          <div className="sales-room-canvas__sections mb-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSectionDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
            >
              <SortableContext
                items={sections.map((section) => section.id)}
                strategy={verticalListSortingStrategy}
              >
                {sections.map((section) => (
                  <SortableSection
                    key={section.id}
                    section={section}
                    sensors={sensors}
                    onChange={(changes) => updateSection(section.id, changes)}
                    onRemove={() => removeSection(section.id)}
                    onRemoveItem={(index) => removeItem(section, index)}
                    onAddContent={() => setSelectorSectionId(section.id)}
                    onCardDragEnd={handleCardDragEnd(section)}
                  />
                ))}
              </SortableContext>
            </DndContext>

            <button
              type="button"
              className="sales-room-canvas__add-section"
              onClick={addSection}
            >
              <AddIcon fontSize="small" /> Add section
            </button>
          </div>
        )}

        {/* ---- Action Plan tab: live buyer progress bar + row editor ------ */}
        {activeTab.key === "milestones" && (
          <section className="sales-room-canvas__milestones mb-4">
            <Typography
              owner="admin"
              variant="h5"
              className="buyer-room__section-title"
            >
              Mutual Action Plan
            </Typography>
            <Typography
              owner="admin"
              variant="body2"
              className="mb-3 sales-room-canvas__hint"
            >
              Buyers report progress on any step from the room, and fully edit
              the ones they add themselves.
            </Typography>
            <div className="buyer-room__plan-progress-card mb-4">
              <div className="buyer-room__progress">
                <div
                  className="buyer-room__progress-bar"
                  style={{
                    width: `${
                      totalMilestones
                        ? Math.round((doneMilestones / totalMilestones) * 100)
                        : 0
                    }%`,
                  }}
                />
              </div>
              <Typography
                owner="admin"
                variant="caption"
                className="buyer-room__progress-label"
              >
                {doneMilestones} of {totalMilestones} steps complete
              </Typography>
            </div>
            <MilestoneBuilder
              milestones={form.milestones}
              setMilestones={(milestones) => setField("milestones", milestones)}
            />
          </section>
        )}
      </div>

      {/* ---- Content picker ---------------------------------------------- */}
      {selectorSectionId && (
        <EntitySelector
          showSelector={!!selectorSectionId}
          setShowSelector={(open) => {
            if (!open) {
              setSelectorSectionId(null);
            }
          }}
          allowedTypes={ROOM_CONTENT_TYPES}
          currentBlock={{ data: { entities: selectorSection?.items || [] } }}
          insertSelections={(selection) => {
            if (selectorSectionId) {
              updateSection(selectorSectionId, { items: selection });
            }
          }}
        />
      )}

      {/* ---- Media pickers (logo / intro video) -------------------------- */}
      {mediaPicker && (
        <MediaLibrary
          user={user}
          csrf_token={user.csrf_token}
          mediaType={mediaPicker === "video" ? "video" : "image"}
          files={{ aspectRatio: null }}
          showMediaLibrary={!!mediaPicker}
          setShowMediaLibrary={(open) => {
            if (!open) {
              setMediaPicker(null);
            }
          }}
          onMediaInsert={(media) => {
            const slot = LOGO_SLOTS.find((s) => s.key === mediaPicker);
            if (slot) {
              setForm((prev) => ({
                ...prev,
                [slot.fidField]: media.fid,
                [slot.urlField]: media.field_media_image || prev[slot.urlField],
              }));
            } else {
              setTempVideo({
                introVideoMid: media.mid || media.fid,
                introVideoName: media.name || media.filename || null,
              });
            }
            setMediaPicker(null);
          }}
        />
      )}
      {/* Video picker dialog */}
      <Dialog
        className="ss-modal"
        open={introVideoDialog}
        onClose={() => {
          setIntroVideoDialog(false);
          setRyoVideo(false);
        }}
        maxWidth={"md"}
        fullWidth={true}
      >
        <Box className={"ss-modal-title"} id="ss-modal-title">
          <Container fluid>
            <Row className="align-items-center justify-content-between">
              <Col xs={"auto"}>
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  Video
                </Typography>
              </Col>
              <Col xs={"auto"}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                >
                  <Button
                    variant="outlined"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="cancel"
                    onClick={() => {
                      if (ryoVideo && hasRecording) {
                        setAlertDetails({
                          show: true,
                          title: "Warning!",
                          message:
                            "You have an unsaved recording. Discard and close?",
                          cancelLabel: "Cancel",
                          confirmLabel: "Confirm",
                          cancel: () => {
                            resetAlert();
                          },
                          confirm: () => {
                            recorderRef.current.discard();
                            setHasRecording(false);
                            setIntroVideoDialog(false);
                            setTempVideo(null);
                            setRyoVideo(false);
                          },
                        });
                      } else {
                        setIntroVideoDialog(false);
                        setTempVideo(null);
                        setRyoVideo(false);
                      }
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Cancel
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="save"
                    className="lh"
                    onClick={handleVideoSave}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Save
                    </Typography>
                  </Button>
                </Stack>
              </Col>
            </Row>
          </Container>
        </Box>
        <DialogContent>
          <Container fluid>
            {tempVideo?.introVideoMid ? (
              <Container className="intro-video-dialog__details px-0">
                <Row className="w-100">
                  <Col xs="6">
                    <video
                      className="intro-video-preview"
                      src={`${serverUrl}/api/v2/assets?mid=${tempVideo?.introVideoMid}`}
                      controls
                      playsInline
                    />
                  </Col>
                  <Col xs="6">
                    <Row className="mb-4">
                      <Col xs={12} className="d-flex flex-column">
                        <Row className="add-video-row w-100">
                          <Col xs={3} className="pe-0">
                            <Typography
                              variant="form-group-label"
                              owner="admin"
                            >
                              File Name:
                            </Typography>
                          </Col>
                          <Col>
                            <Typography
                              variant="form-group-label"
                              owner="admin"
                              color="adminText.dark"
                              weight="light"
                            >
                              {tempVideo?.introVideoName}
                            </Typography>
                          </Col>
                        </Row>
                        <Row className="add-video-row w-100">
                          <Col xs={3} className="pe-0">
                            <Typography
                              variant="form-group-label"
                              owner="admin"
                            >
                              MID:
                            </Typography>
                          </Col>
                          <Col>
                            <Typography
                              variant="form-group-label"
                              owner="admin"
                              color="adminText.dark"
                              weight="light"
                            >
                              {tempVideo?.introVideoMid}
                            </Typography>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    <Row className="btn-grp justify-content-between">
                      <Col xs={"auto"}>
                        <Button
                          className="change-btn me-0"
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setMediaPicker("video");
                          }}
                        >
                          <Typography
                            owner="admin"
                            variant="button"
                            className="lh"
                          >
                            Change video
                          </Typography>
                        </Button>
                      </Col>
                      <Col xs={"auto"}>
                        <Button
                          className="remove-btn me-0"
                          variant="contained"
                          color="error"
                          onClick={() => {
                            setTempVideo({
                              introVideoMid: "",
                              introVideoName: "",
                            });
                          }}
                        >
                          <Typography
                            owner="admin"
                            variant="button"
                            className="lh"
                          >
                            Remove video
                          </Typography>
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Container>
            ) : ryoVideo ? (
              <VideoRecorder
                ref={recorderRef}
                onDirtyChange={setHasRecording}
                onUploaded={handleRecordingUploaded}
              />
            ) : (
              <Container className="intro-video-dialog__picker">
                <Row className="d-flex justify-content-center">
                  <Col xs={6}>
                    <Row
                      className="flex-column text-center align-items-center"
                      style={{ gap: "50px" }}
                    >
                      <Col xs={10} className="intro-video-dialog__hint">
                        <Typography variant="body2" owner="admin" lh="base">
                          To attach an intro video select or upload a video file
                          via the asset library.
                        </Typography>
                      </Col>
                      <Col>
                        <Button
                          className="media-lib-btn"
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setMediaPicker("video");
                          }}
                        >
                          <Typography
                            variant="button"
                            owner="admin"
                            className="lh"
                          >
                            Asset Library
                          </Typography>
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={6}>
                    <Row
                      className="flex-column text-center align-items-center"
                      style={{ gap: "50px" }}
                    >
                      <Col xs={10} className="intro-video-dialog__hint">
                        <Typography variant="body2" owner="admin" lh="base">
                          To record a new video, use the recording feature via
                          the button below.
                        </Typography>
                      </Col>
                      <Col>
                        <Button
                          className="media-lib-btn"
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setRyoVideo(true);
                          }}
                        >
                          <Typography
                            variant="button"
                            owner="admin"
                            className="lh"
                          >
                            Record intro video
                          </Typography>
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Container>
            )}
          </Container>
          <AlertModal
            showAlert={alertDetails.show}
            alertType={"warning"}
            alertMessageTitle={alertDetails.title}
            alertMessageBody={alertDetails.message}
            cancelButton={true}
            cancelButtonLabel={alertDetails.cancelLabel}
            confirmButton={true}
            confirmButtonLabel={alertDetails.confirmLabel}
            confirmButtonColor={"primary"}
            onConfirm={() => {
              if (alertDetails.confirm) {
                alertDetails.confirm();
              }
            }}
            onCancel={() => {
              if (alertDetails.cancel) {
                alertDetails.cancel();
              }
            }}
            showAlertCallback={() => {
              setAlertDetails({
                show: false,
                title: "",
                message: "",
                cancelLabel: "",
                confirmLabel: "",
                cancel: () => {},
                confirm: () => {},
              });
            }}
          />
        </DialogContent>
      </Dialog>
      {/* ---- Settings dialog (non-visual room options) ------------------- */}
      <Dialog
        open={!!showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        className="sales-room-canvas__settings"
      >
        <div className="sales-room-canvas__settings-head">
          <Typography owner="admin" variant="h6">
            Room settings
          </Typography>
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => setShowSettings(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>

        <DialogContent dividers className="sales-room-canvas__settings-body">
          <div className="form-group">
            <label>Status</label>
            <CategorySelector
              name="roomStatus"
              label="Status"
              categories={ROOM_STATUSES}
              selectedCategory={ROOM_STATUSES.find(
                (status) => status.value === form.status,
              )}
              outlined
              variant="outlined"
              size="small"
              handleCategorySelection={(value) => setField("status", value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="room-expiry">Expires</label>
            <TextField
              id="room-expiry"
              className="date-input-field w-100"
              type="datetime-local"
              variant="outlined"
              size="small"
              value={form.expiry}
              onChange={(e) => setField("expiry", e.target.value)}
              helperText="Leave empty for no expiry"
            />
          </div>

          <div className="form-group">
            <label>Link access</label>
            <CategorySelector
              name="roomAccess"
              label="Link access"
              categories={ACCESS_MODES}
              selectedCategory={ACCESS_MODES.find(
                (mode) => mode.value === (form.accessMode || "link"),
              )}
              outlined
              variant="outlined"
              size="small"
              handleCategorySelection={(value) => setField("accessMode", value)}
            />
          </div>

          {form.accessMode === "password" && (
            <div className="form-group">
              <label htmlFor="room-password">Room passcode</label>
              <TextField
                id="room-password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                size="small"
                autoComplete="new-password"
                value={form.password || ""}
                placeholder="No passcode"
                helperText="Share it with your buyer separately from the room link."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={showPassword ? "Hide passcode" : "Show passcode"}
                      >
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    password: e.target.value,
                    passwordDirty: true,
                  }))
                }
              />
            </div>
          )}

          {form.accessMode === "restricted" && (
            <Typography owner="admin" variant="body2" className="mb-2">
              Buyers verify their email with a secure link - only people listed
              under Buyer contacts below have access.
            </Typography>
          )}

          <div className="form-group">
            <label>Visit alerts</label>
            <CategorySelector
              name="roomAlerts"
              label="Visit alerts"
              categories={ALERT_MODES}
              selectedCategory={ALERT_MODES.find(
                (mode) => mode.value === form.alertMode,
              )}
              outlined
              variant="outlined"
              size="small"
              handleCategorySelection={(value) => setField("alertMode", value)}
            />
            <FormControlLabel
              className="mt-1"
              control={
                <Checkbox
                  size="small"
                  checked={!!form.dailyDigest}
                  onChange={(e) => setField("dailyDigest", e.target.checked)}
                />
              }
              label={
                <Typography owner="admin" variant="body2">
                  Daily digest email (only on days with activity)
                </Typography>
              }
            />
          </div>

          {canManageTemplates && (
            <div className="form-group">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!form.isTemplate}
                    onChange={(e) => setField("isTemplate", e.target.checked)}
                  />
                }
                label={
                  <Typography owner="admin" variant="body2">
                    Save as template (visible to all sellers)
                  </Typography>
                }
              />
            </div>
          )}

          <div className="sales-room-canvas__settings-section">
            <Typography owner="admin" variant="h6" className="ss-subtitle mb-2">
              {form.accessMode === "restricted"
                ? "Buyer contacts (access list)"
                : "Buyer contacts"}
            </Typography>
            {form.contacts.map((contact, index) => (
              <div
                className="sales-room-canvas__contact"
                key={`contact-${index}`}
              >
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={contact.name}
                  placeholder="Name"
                  onInput={(e) => updateContact(index, "name", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  type="email"
                  value={contact.email}
                  placeholder="Email"
                  onInput={(e) => updateContact(index, "email", e.target.value)}
                />
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={contact.role}
                  placeholder="Role"
                  onInput={(e) => updateContact(index, "role", e.target.value)}
                />
                <div className="sales-room-canvas__contact-actions">
                  {form.accessMode === "restricted" &&
                    onSendAccessLink &&
                    contact.email && (
                      <Tooltip title="Send access link to this contact">
                        <IconButton
                          size="small"
                          onClick={() => onSendAccessLink(contact.email)}
                        >
                          <SendLinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  <Tooltip title="Remove contact">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setField(
                          "contacts",
                          form.contacts.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))}
            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              startIcon={<AddIcon />}
              onClick={() =>
                setField("contacts", [
                  ...form.contacts,
                  { name: "", email: "", role: "" },
                ])
              }
            >
              Add contact
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
